// © Microsoft Corporation. All rights reserved.

import { DateTimeFormat } from "./DateTimeFormat";
import { LocalizableTimeStrings } from "./LocalizableTimeStrings";
import { LocalizationUtility } from "./LocalizationUtility";
import { ResponsiveDateTransformer } from "./ResponsiveDateTransformer";

/**
 * Default value for `timeLocStrings`.
 */
const localizableTimeStrings: LocalizableTimeStrings = {
    JustNow: "Just Now",
    NumberMinuteAgo: "{0} minute ago",
    NumberMinutesAgo: "{0} minutes ago",
    NumberHourAgo: "{0} hour ago",
    NumberHoursAgo: "{0} hours ago",
    NumberDayAgo: "{0} day ago",
    NumberDaysAgo: "{0} days ago",
    NumberWeekAgo: "{0} week",
    NumberWeeksAgo: "{0} weeks",
    NumberMonthAgo: "{0} month",
    NumberMonthsAgo: "{0} months",
    NumberYearAgo: "{0} year",
    NumberYearsAgo: "{0} years",
    TimeYesterday: "Yesterday at {0}",
    TimeDayOfWeek: "{0} at {1}"
};

/**
 * Values for Arabic languages
 */
const arabicLocalizableTimeStrings = {
    Minute: {
        one: "{0} دقيقة",
        two: "دقيقتان",
        threeToTen: "{0} دقائق",
        elevenPlus: "{0} دقيقة"
    },
    Hour: {
        one: "{0} ساعة",
        two: "ساعتان",
        threeToTen: "{0} ساعات",
        elevenPlus: "{0} ساعة"
    },
    Day: {
        one: "{0} يوم",
        two: "يومان",
        threeToTen: "{0} أيام",
        elevenPlus: "{0} يوما"
    },
    Week: {
        one: "{0} أسبوع",
        two: "{0} أسبوع",
        threeToTen: "{0} أسابيع",
        elevenPlus: "{0} أسبوعا"
    },
    Month: {
        one: "{0} شهر",
        two: "{0} شهر",
        threeToTen: "{0} أشهر",
        elevenPlus: "{0} شهرا"
    },
    Year: {
        one: "{0} عام",
        two: "{0} عام",
        threeToTen: "{0} أعوام",
        elevenPlus: "{0} عاما"
    }
};

/**
 * Values for Hebrew Languages
 **/
const hebrewLocalizableTimeStrings = {
    Week: {
        one: "{0} שבוע",
        two: "{0} שבועיים",
        three: "{0} שבועות"
    },
    Month: {
        one: "{0} חודש",
        two: "{0} חודשיים",
        three: "{0} חודשים"
    },
    Year: {
        one: "{0} שנה",
        two: "{0} שנתיים",
        three: "{0} שנים"
    }
};

/**
 * Wrapper function which converts ISO format timestamp to Readable format
 * Example - "2019-06-22T00:37:40.584Z" might be converted to "4h ago"
 * @param time time that needs to be converted in readable format
 * @param locale Locale string
 * @param timeLocStrings set of responsive localizable strings
 * @param dateTimeFormat format to use for conversion
 * @param locUtility utility object used to produce date string
 * @param useGranularStrings use granular strings, e.g. 1 week ago or 2 weeks ago
 */
export function getDateString(
    time: Date,
    locale: string,
    timeLocStrings?: LocalizableTimeStrings,
    dateTimeFormat?: DateTimeFormat,
    locUtility?: LocalizationUtility,
    useGranularStrings?: boolean): string {

    const currentLocale = locale && locale.toLowerCase();

    if (!(time instanceof Date && !isNaN(time.valueOf()))) {
        return "";
    }

    if (useGranularStrings && timeLocStrings) {
        return getGranularTimestamp(time, new Date(), timeLocStrings, currentLocale);
    }

    // If the loc strings aren't available and we're in ZH-CN, then return empty string instead of using English fallback strings
    if (!timeLocStrings && currentLocale === "zh-cn") {
        return "";
    }

    if (!timeLocStrings) {
        timeLocStrings = localizableTimeStrings;
    }

    locUtility = locUtility || new LocalizationUtility();
    const fallbackText: string = locUtility.localizedDateString(time, currentLocale);

    if (!dateTimeFormat) {
        dateTimeFormat = DateTimeFormat.Common;
    }

    const responsiveDatePipeline = new ResponsiveDateTransformer(timeLocStrings, currentLocale, locUtility);

    return responsiveDatePipeline.transform(time, fallbackText, dateTimeFormat);
}

/**
 * Utility function to get the friendly time stamp to be displayed with the news Item after provider text, ex: "1 hr"
 * @param publishedDateTime Published Date/Time of the content feed
 * @param localizedStrings TimeStamp config setting
 * @param locale the Locale, eg, en-us, ja-jp, ar-sa
 */
export function getGranularTimestamp(publishedDateTime: Date, currentTime: Date, localizedStrings: any, locale: string): string {
    if (!(publishedDateTime instanceof Date) || isNaN(publishedDateTime.valueOf())) {
        return "";
    }

    const msPerMinute: number = DateTimeConstants.MsPerMinute;
    const msPerHour: number = DateTimeConstants.MsPerHour;
    const msPerDay: number = DateTimeConstants.MsPerDay;
    const msPerWeek: number = DateTimeConstants.MsPerWeek;
    const msPerMonth: number = DateTimeConstants.MsPerMonth;
    const msPerYear: number = DateTimeConstants.MsPerYear;

    const elapsed: number = currentTime.getTime() - publishedDateTime.getTime();

    if (elapsed < msPerMinute) {
        // If less than a minute or negative, show just now
        return localizedStrings.JustNow || "";
    }

    // Use years as default
    let timeDenominator = msPerYear;
    let timeSelection = "Year";

    if (elapsed < msPerHour) {
        // If less than an hour, show minutes
        timeDenominator = msPerMinute;
        timeSelection = "Minute";
    } else if (elapsed < msPerDay) {
        // If less than a day, show hours
        timeDenominator = msPerHour;
        timeSelection = "Hour";
    }  else if (elapsed < msPerWeek) {
        // If less than 7 days, show days
        timeDenominator = msPerDay;
        timeSelection = "Day";
    } else if (elapsed < msPerMonth) {
        // If less than a month, show weeks
        timeDenominator = msPerWeek;
        timeSelection = "Week";
    } else if (elapsed < msPerYear) {
        // If less than a year, show months
        timeDenominator = msPerMonth;
        timeSelection = "Month";
    }

    const isArabicLanguage = /^[Aa][Rr]-/.test(locale);
    const isHebrewLanguage = /^[Hh][Ee]-/.test(locale);
    const timeValue = Math.floor(elapsed / timeDenominator);
    let displayFormat = null;
    if (isArabicLanguage) {
        displayFormat = getArabicFormat(timeValue, timeSelection);
    } else if (isHebrewLanguage) {
        displayFormat =  getHebrewFormat(localizedStrings, timeValue, timeSelection);
    } else {
        displayFormat =  localizedStrings[`Number${timeSelection}${timeValue > 1 ? "s" : ""}Ago`];
    }
    return displayFormat ? displayFormat.replace("{0}", timeValue) : "";
}

/**
 * Helper to get proper date format string for Arabic languages
 * @param timeValue amount of time since published
 * @param timeUnit unit of time
 * @returns display format string
 */
function getArabicFormat(timeValue: number, timeUnit: string): string {
    if (timeValue < 2) {
        return arabicLocalizableTimeStrings[timeUnit].one;
    }

    if (timeValue < 3) {
        return arabicLocalizableTimeStrings[timeUnit].two;
    }

    if (timeValue < 11) {
        return arabicLocalizableTimeStrings[timeUnit].threeToTen;
    }

    return arabicLocalizableTimeStrings[timeUnit].elevenPlus;
}

/**
 * Helper to get proper date format string for Hebrew languages
 * @param localizedStrings for minute, hour, day formatting.
 * @param timeValue amount of time since published
 * @param timeUnit unit of time
 * @returns display format string
 */
function getHebrewFormat(localizedStrings: LocalizableTimeStrings, timeValue: number, timeUnit: string): string {
    // If minute, hour, day then show the timestamps how it would read in singular/plural English
    if (timeUnit == "Minute" || timeUnit == "Hour" || timeUnit == "Day") {
        return localizedStrings[`Number${timeUnit}${timeValue > 1 ? "s" : ""}Ago`];
    }
    // For weeks, months, year, then use the other hebrew strings defined as per the spec
    if (timeValue < 2) {
        return hebrewLocalizableTimeStrings[timeUnit].one;
    }
    if (timeValue < 3) {
        return hebrewLocalizableTimeStrings[timeUnit].two;
    }
    return hebrewLocalizableTimeStrings[timeUnit].three;

}

/**
 * Experiment helper which reformats timestamps in JA-JP to MM/DD hh:mm, in 24 hour format. AM/PM strings will be removed
 * Related flight ID : prg-1sw-tsjp
 * @param time amount of time since published
 * @returns display format string
 */
export function getNewTimestampFormatForJapanMarkets(time: Date): string {
    return time.toLocaleString("ja-JP", {
        hour12: false, hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", timeZone: "Asia/Tokyo"
    }).replace("午後", "").replace("午前", "");
}

/** Datetime constants. */
const DateTimeConstants = {
    MsPerMinute: 60 * 1000,
    MsPerHour: 60 * 60 * 1000,
    MsPerDay: 24 * 60 * 60 * 1000,
    MsPerWeek: 7 * 24 * 60 * 60 * 1000,
    MsPerMonth: 30 * 24 * 60 * 60 * 1000,
    MsPerYear: 12 * 30 * 24 * 60 * 60 * 1000
};
